import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { paymentsSentry } from '@frontend/payments-hooks';
import {
  justifiTokenizePayloadToPaymentMethod,
  JustifiTokenizePaymentMethod,
  TokenizePaymentMethodErrorCallbackPayloads,
  TokenizePaymentMethodSubmitSuccessEvent,
  useJustifiLoadedState,
  useJustifiTokenizePaymentMethod,
} from '@frontend/payments-justifi-wrapper';
import { theme } from '@frontend/theme';
import { ContentLoader, EmailField, useAlert, useControlledField } from '@frontend/design-system';
import { useInvoicePerson, useValidateACHBalance } from '../../../../hooks';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { useUpdatePaidRequestPoll } from '../../../hooks/use-update-paid-request-poll';
import { devLogger } from '../../../utils/logs';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { useJustifiManualCardEntryPayment } from './manual-card-entry-justifi/use-justifi-manual-card-entry-payment';
import { ManualCardEntryNavigationSwitch } from './manual-card-entry-navigation-switch';
import { ManualCardEntryStepBodyProps } from './manual-card-entry.types';

const DATA_TRACKING_PREFIX = `pay-client-virtualterminal`;

const styles = {
  emailField: css`
    margin-bottom: ${theme.spacing(1.5)};
  `,
  billingAddressTitle: css`
    margin: ${theme.spacing(2, 0)};
  `,
};

export const ManualCardEntryJustifi = () => {
  return <ManualCardEntryNavigationSwitch body={ManualCardEntryJustifiBody} />;
};

const ManualCardEntryJustifiBody = ({ onGoBack, onCompleteStep, onPaymentSuccess }: ManualCardEntryStepBodyProps) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();

  const errorAlert = (message: string) =>
    alerts.error({
      autoDismissAfter: 12000,
      message,
    });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    paymentMethod: { isLoaded },
  } = useJustifiLoadedState(['paymentMethod']);

  const {
    invoice,
    activeLocationId,
    invoiceQuery: { refetch: refetchInvoice },
    activeGateway,
  } = useCollectPaymentMultiContext();

  const { person, personEmail /* personFullName, personAddress */ } = useInvoicePerson(invoice);

  const [emailReceipt, setEmailReceipt] = useState<string | undefined>(personEmail);

  const { value: receiptEmail, ...receiptEmailProps } = useControlledField({
    required: false,
    value: emailReceipt,
    type: 'email',
    onChange: (value) => {
      setEmailReceipt(value);
    },
  });

  useEffect(() => {
    if (person) setEmailReceipt(personEmail);
  }, [person]);

  const { setProcessingAndStartPoll } = useUpdatePaidRequestPoll(invoice, () => {
    onPaymentSuccess();
  });

  const onRefetchInvoice = useCallback(() => {
    return refetchInvoice().then((res) => res.data);
  }, [refetchInvoice]);

  const { makeManualCardEntryPayment, makingManualCardEntryPayment } = useJustifiManualCardEntryPayment({
    cardName: 'TODO: What should this be?',
    receiptEmail,
    invoice,
    onInvoiceAlreadyPaid: onGoBack,
    onRefetchInvoice,
  });

  const { isACHBalanceValidated } = useValidateACHBalance({
    amount: invoice?.billedAmount ?? 0,
    locationId: activeLocationId,
  });

  const onErrorCallback = (data: TokenizePaymentMethodErrorCallbackPayloads) => {
    if (data.message) {
      errorAlert(data.message);
    } else {
      errorAlert(t('An unknown error occurred processing your payment. Please try again or contact support'));
    }
  };

  const onSubmitCallback = async (data: TokenizePaymentMethodSubmitSuccessEvent) => {
    try {
      const balanceValidated = await isACHBalanceValidated();
      if (!balanceValidated) {
        errorAlert(t('Bank account does not have sufficient balance'));
        return;
      }
    } catch (err) {
      console.error(err);
      errorAlert(t('Failed to check bank account balance'));
      if (err instanceof Error) {
        paymentsSentry.error('Failed to validate ACH balance', 'ManualCardEntry', err.message);
      } else {
        paymentsSentry.error('Failed to validate ACH balance', 'ManualCardEntry', 'Unknown error');
      }
      return;
    }

    try {
      const response = await makeManualCardEntryPayment({
        paymentMethodId: data.token,
        paymentMethod: justifiTokenizePayloadToPaymentMethod(data),
      });

      devLogger.log('JustiFi -> Manual card entry payment response', response);

      setProcessingAndStartPoll();
      onCompleteStep();
    } catch (err) {
      console.warn('JustiFi -> Error creating manual card entry payment', err);

      errorAlert(t('Failed to process payment.'));

      if (err instanceof Error) {
        paymentsSentry.error('Failed to process manual card entry payment', 'ManualCardEntry', err.message);
      } else {
        paymentsSentry.error('Failed to process manual card entry payment', 'ManualCardEntry', 'Unknown error');
      }
      return;
    }

    return true;
  };

  const { justifiProps, elementRef, tokenizePaymentMethod } = useJustifiTokenizePaymentMethod({
    locationId: activeLocationId,
    justifiProps: {
      disableBankAccount: true, // only show card option
      hideCardBillingForm: false, // if true, shows only zip code field
    },
    onError: onErrorCallback,
    onSubmit: onSubmitCallback,
  });

  const onClickSubmit = async () => {
    setIsSubmitting(true);
    await tokenizePaymentMethod();
    setIsSubmitting(false);
  };

  const { backButton: backButtonTrackingId, primaryButton: primaryButtonTrackingId } = useScopedTrackingIds({
    backButton: `manual-card-entry--${activeGateway}--back-btn`,
    primaryButton: `manual-card-entry--${activeGateway}--primary-btn`,
  });

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.manualCardEntry}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.manualCardEntry]}
      isValid={!makingManualCardEntryPayment}
      footer={{
        primary: {
          children: t('Complete Payment'),
          loading: isSubmitting,
          trackingId: primaryButtonTrackingId,
        },
        back: {
          children: t('Back'),
          trackingId: backButtonTrackingId,
        },
        onClickPrimary: onClickSubmit,
        onClickBack: onGoBack,
      }}
    >
      <form>
        <ContentLoader show={!isLoaded} />
        <EmailField
          name='email'
          label={t('Email Receipt (optional)')}
          {...receiptEmailProps}
          actionText={'Clear'}
          data-trackingid={`${DATA_TRACKING_PREFIX}-txt-emailreceipt`}
          onActionClick={emailReceipt ? () => setEmailReceipt('') : undefined}
          hasNonFloatingLabel={true}
          css={styles.emailField}
          value={receiptEmail}
        />

        <JustifiTokenizePaymentMethod elementRef={elementRef} {...justifiProps} />
      </form>
    </StyledStep>
  );
};
